import React, { useEffect, useState } from "react";
import { Logo } from "assets/svg";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { fetchRegisterCompanyInfo } from "reducers/informationSlice";
import { message } from "antd";
import { UserTypeEnum } from "common/enums/UserType.enum";

const AcceptTermService: React.FC = () => {
  const [agreed, setAgreed] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { informationData, registrationFormData } = useSelector(
    (state: RootState) => state.information,
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const tradeRules = [
    "廃棄物の出品・購入に関するガイドライン",
    "禁止行為・責任の所在・遵守事項",
    "不適切な出品物の取り扱い など",
  ];

  const platformTerms = [
    "本サービスの提供範囲・利用者の責任",
    "登録情報の取り扱いと審査に関する事項",
    "本人確認・法人確認の重要性 など",
  ];

  const handleSubmit = () => {
    const token = searchParams.get("token");
    const type = searchParams.get("type");

    if (agreed && token && registrationFormData) {
      dispatch(
        fetchRegisterCompanyInfo(
          {
            ...registrationFormData,
            ...{
              type: Number(type),
            },
          },
          token,
        ),
      );
    } else {
      message.error("利用規約に同意してください。");
    }
  };

  useEffect(() => {
    if (informationData) {
      navigate("/register-complete");
    }
  }, [informationData]);

  return (
    <main className="bg--blur box">
      <div className="w-full relative rounded-[20px] border-[8px] border-white bg-primary-light shadow-[0_0_15px_0_rgba(0,0,0,0.05)] flex flex-col items-center justify-center py-[52px] px-[22px] sm:max-w-[1020px] sm:py-[72px] sm:px-[62px]">
        <Logo className="flex justify-center items-center w-[200px] h-[81px] aspect-[1/1] mb-[30px] sm:w-[300px] sm:h-[121px] sm:mb-[40px]" />
        <h2 className="text-[20px] font-bold text-primary mb-[10px] sm:text-[28px] sm:mb-[15px] text-center">
          利用規約の同意
        </h2>
        <p className="text-[14px] text-center mb-[24px] sm:text-[16px] sm:mb-[32px]">
          ご利用にあたって、
          <br className="sm:hidden" />
          以下の内容を必ずご確認ください。
        </p>

        {/* Section 1: Trade Rules */}
        <div className="w-full mb-[24px] sm:mb-[32px]">
          <h3 className="text-primary font-bold text-[16px] mb-[10px] flex items-center sm:text-[18px]">
            <span className="border-l-4 border-primary mr-[10px] pl-[8px]">
              1. 取引ルール
            </span>
          </h3>
          <hr className="border-gray-300 mb-[10px]" />
          <div className="sm:grid sm:gap-[40px]">
            <ul className="list-disc pl-[20px] text-[14px] sm:text-[16px]">
              {tradeRules.map((item, i) => (
                <li key={i} className="mb-[6px] sm:mb-[10px]">
                  {item}
                </li>
              ))}
            </ul>
            <div className="flex flex-col items-center justify-center text-[30px] select-none">
              <span>・</span>
              <span>・</span>
              <span>・</span>
            </div>
          </div>
        </div>

        {/* Section 2: Platform Terms */}
        <div className="w-full mb-[24px] sm:mb-[32px]">
          <h3 className="text-primary font-bold text-[16px] mb-[10px] flex items-center sm:text-[18px]">
            <span className="border-l-4 border-primary mr-[10px] pl-[8px]">
              2. プラットフォーム利用条件
            </span>
          </h3>
          <hr className="border-gray-300 mb-[10px]" />
          <div className="sm:grid sm:gap-[40px]">
            <ul className="list-disc pl-[20px] text-[14px] sm:text-[16px]">
              {platformTerms.map((item, i) => (
                <li key={i} className="mb-[6px] sm:mb-[10px]">
                  {item}
                </li>
              ))}
            </ul>
            <div className="flex flex-col items-center justify-center text-[30px] select-none">
              <span>・</span>
              <span>・</span>
              <span>・</span>
            </div>
          </div>
        </div>

        {/* Agreement */}
        <div className="w-full mb-[24px] sm:mb-[32px]">
          <h4 className="text-primary font-bold text-[15px] mb-[10px] sm:text-[16px]">
            同意事項
          </h4>
          <div className="flex items-center mb-[10px]">
            <input
              id="agree"
              type="checkbox"
              checked={agreed}
              onChange={() => setAgreed(!agreed)}
              className="w-[22px] h-[22px] border-2 border-gray-300 rounded mr-[10px] accent-primary"
            />
            <label htmlFor="agree" className="text-[14px] sm:text-[16px]">
              上記の取引ルールおよび利用条件に同意します
            </label>
          </div>
        </div>

        {/* Buttons */}
        <div className="w-full flex flex-col-reverse sm:flex-row gap-[12px] sm:gap-[20px] sm:justify-center">
          <Link
            to={
              Number(searchParams.get("type")) === UserTypeEnum.COMPANY_SELLER
                ? `/verify-information?token=${searchParams.get("token")}&type=${searchParams.get("type")}`
                : `/information?token=${searchParams.get("token")}&type=${searchParams.get("type")}`
            }
            type="button"
            className="text-center w-full bg-white border border-primary text-primary font-bold py-[13px] rounded-[6px] text-[16px] sm:w-[300px] sm:text-[16px] sm:order-1"
          >
            企業情報の入力へ戻る
          </Link>
          <button
            onClick={handleSubmit}
            type="submit"
            disabled={!agreed}
            className={`w-full bg-primary text-white font-bold py-[13px] rounded-[6px] text-[16px] sm:w-[300px] sm:text-[16px] sm:order-2 ${!agreed ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            登録申請する
          </button>
        </div>
      </div>
    </main>
  );
};

export default AcceptTermService;
